import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { PropertyFilter } from 'support/types/filters';
import { Input } from 'components/Form/Input/Input/Input';

type DropdownValues = NonNullable<PropertyFilter['dropDownOptions']>['dropdownValues'];

type DropdownPanelShellProps = {
  filter: PropertyFilter;
  // Container sizing; sub-option panels are wider to fit their inline controls.
  className?: string;
  // Rendered at the top of the list, before the options (e.g. an "All values" row).
  header?: ReactNode;
  // Receives the search-filtered options and renders the option rows.
  children: (filteredOptions: DropdownValues) => ReactNode;
};

// Shared chrome for every dropdown filter panel: search box, query state, option
// filtering, the loading/empty guards, and the scrollable list container. The
// variant-specific part (how each row and any sub-options render) is the `children`
// render prop, so panels only own their selection behaviour.
export const DropdownPanelShell: FC<DropdownPanelShellProps> = ({
  filter,
  className = 'flex max-h-80 w-64 flex-col',
  header,
  children,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const options = filter.dropDownOptions;

  const filteredOptions = useMemo(() => {
    const values = options?.dropdownValues ?? [];
    if (!query.trim()) {
      return values;
    }
    const normalizedQuery = query.trim().toLowerCase();
    return values.filter((option) => t(option.label).toLowerCase().includes(normalizedQuery));
  }, [options?.dropdownValues, query, t]);

  if (!options) {
    return null;
  }

  if (options.isLoading) {
    return <div className="w-64 px-4 py-3 text-sm text-gray-500">{t('common:loading')}</div>;
  }

  return (
    <div className={className}>
      <div className="border-b border-gray-100 p-2">
        <Input
          type="search"
          value={query}
          placeholder={t('common:search.placeholder')}
          leftIcon={<MagnifyingGlassIcon className="size-5" />}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          id={`filter-panel-search-${filter.key}`}
        />
      </div>

      {/* Styling ::-webkit-scrollbar opts out of macOS overlay scrollbars, so the thumb stays visible and
          signals that the list holds more options. Setting scrollbar-width/scrollbar-color here would
          make Chromium ignore these rules again. */}
      <ul
        data-testid={`filter-panel-options-${filter.key}`}
        className="overflow-y-auto py-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-transparent"
      >
        {header}
        {children(filteredOptions)}
      </ul>
    </div>
  );
};
